import React, { useState } from 'react';
import Diamond from './Diamond';

// Zone configurations
const ZONES = {
  red: { label: 'RED', sublabel: 'Below $4,000', description: 'Red: Half month expenses or less. Survival rules are active. Every dollar is tracked. Revenue work dominates.' },
  'yellow-red': { label: 'YEL-RED', sublabel: '$4K - $6K', description: 'Yellow-Red: Half month plus rent. Getting tight. Auto-payments paused. Revenue-within-1-week outsourcing rule active.' },
  yellow: { label: 'YELLOW', sublabel: '$6K - $8K', description: 'Yellow: Month expenses covered. Tight but stable. Auto-payments return to normal. Full discretionary.' },
  'green-yellow': { label: 'GRN-YEL', sublabel: '$8K - $10K', description: 'Green-Yellow: Month expenses plus rent. Comfortable but no buffer. Outsourcing doubles. Building momentum.' },
  green: { label: 'GREEN', sublabel: '$10,000+', description: 'Green: Full flexibility. Month expenses plus rent plus 2-week buffer. Build the future.' }
};

// Gate definitions for each zone path
const ZONE_GATES = {
  red: [
    { id: 'r1', question: 'Is this a credit card payment?', yesExit: { text: 'Do not pay. $0 credit card payments in Red.', sub: 'Do not keep cash in accounts where auto-withdrawals can grab it.' } },
    { id: 'r2', question: 'Is this an automatic payment or subscription?', yesGate: 'r2a' },
    { id: 'r2a', question: 'Is the service facing suspension within 7 days?', parent: 'r2', yesGate: 'r2b', noExit: { text: 'Hold. Do not pay.', sub: 'Suspension is not imminent. Keep your cash.' } },
    { id: 'r2b', question: 'Is it required for revenue generation or basic life function?', parent: 'r2a', yesResult: { text: 'Pay it. Keep the tool alive.' }, noExit: { text: 'Let it suspend.', sub: 'Not essential for revenue or survival. It can wait.' } },
    { id: 'r3', question: 'Is this a debt to an individual?', context: 'Friends, vendors, or anyone you owe personally.', yesGate: 'r3a' },
    { id: 'r3a', question: 'Is it the 15th and you have not made your $50 minimum this month?', parent: 'r3', yesResult: { text: 'Pay $50. Protect the relationship.' }, noExit: { text: 'Hold. You have already paid or it is not the 15th yet.' } },
    { id: 'r4', question: 'Is this an outsourcing expense?', yesGate: 'r4a' },
    { id: 'r4a', question: 'Is it $25/hr or less, does it enable revenue within 1 week, and are you within the $700/month budget?', context: 'All three must be true.', parent: 'r4', yesLabel: 'ALL YES', noLabel: 'ANY NO', yesResult: { text: 'Spend. This outsourcing serves revenue.' }, noExit: { text: 'Do not outsource. Do it yourself or skip it.' } },
    { id: 'r5', question: 'Is this discretionary spending?', context: 'Eating out, personal items, entertainment, non-essentials.', yesGate: 'r5a', noExit: { text: 'Default: Do not spend.', sub: 'Flag this expense and decide in your weekly review.', label: 'UNCATEGORIZED' } },
    { id: 'r5a', question: 'Do you have remaining budget from your $62.50 this week?', context: 'Red zone: discretionary is $125/week total, $62.50 each.', parent: 'r5', yesResult: { text: 'Spend. Within your weekly discretionary.' }, noExit: { text: 'Do not spend. Discretionary is tapped for the week.' } }
  ],
  'yellow-red': [
    { id: 'yr1', question: 'Is this a credit card payment?', yesExit: { text: 'Do not pay. $0 credit card payments in Yellow-Red.' } },
    { id: 'yr2', question: 'Is this an automatic payment or subscription?', yesGate: 'yr2a' },
    { id: 'yr2a', question: 'Is the service facing suspension within 7 days?', parent: 'yr2', yesGate: 'yr2b', noExit: { text: 'Hold. Do not pay.' } },
    { id: 'yr2b', question: 'Is it required for revenue generation or basic life function?', parent: 'yr2a', yesResult: { text: 'Pay it. Keep the tool alive.' }, noExit: { text: 'Let it suspend.' } },
    { id: 'yr3', question: 'Is this a debt to an individual?', yesGate: 'yr3a' },
    { id: 'yr3a', question: 'Is it the 15th and you have not made your $50 minimum this month?', parent: 'yr3', yesResult: { text: 'Pay $50. Protect the relationship.' }, noExit: { text: 'Hold.' } },
    { id: 'yr4', question: 'Is this an outsourcing expense?', yesGate: 'yr4a' },
    { id: 'yr4a', question: 'Is it $25/hr or less, does it enable revenue within 1 week, and are you within the $700/month budget?', parent: 'yr4', yesLabel: 'ALL YES', noLabel: 'ANY NO', yesResult: { text: 'Spend. This outsourcing serves revenue.' }, noExit: { text: 'Do not outsource.' } },
    { id: 'yr5', question: 'Is this discretionary spending?', yesGate: 'yr5a', noExit: { text: 'Default: Do not spend. Flag for weekly review.', label: 'UNCATEGORIZED' } },
    { id: 'yr5a', question: 'Do you have remaining budget from your $125 this week?', context: 'Yellow-Red: discretionary is $250/week total, $125 each.', parent: 'yr5', yesResult: { text: 'Spend. Within your weekly discretionary.' }, noExit: { text: 'Do not spend. Discretionary is tapped.' } }
  ],
  yellow: [
    { id: 'y1', question: 'Is this a credit card payment?', yesExit: { text: 'Do not pay. $0 until sustained Yellow for 2+ weeks.' } },
    { id: 'y2', question: 'Is this a debt to an individual?', yesGate: 'y2a' },
    { id: 'y2a', question: 'Is it the 15th and you have not made your $50 minimum?', parent: 'y2', yesResult: { text: 'Pay $50.' }, noExit: { text: 'Optional. Your call.' } },
    { id: 'y3', question: 'Is this an outsourcing expense?', yesGate: 'y3a' },
    { id: 'y3a', question: 'Is it $25/hr or less and within the $700/month budget?', parent: 'y3', yesResult: { text: 'Spend. Outsourcing approved.' }, noExit: { text: 'Do not outsource.' } },
    { id: 'y4', question: 'Is this discretionary spending?', yesGate: 'y4a' },
    { id: 'y4a', question: 'Do you have remaining budget from your $125 this week?', context: 'Yellow: full discretionary. $250/week total, $125 each.', parent: 'y4', yesResult: { text: 'Spend.' }, noExit: { text: 'Do not spend. Discretionary is tapped.' } },
    { id: 'y5', question: 'Is this a new business expense or subscription?', yesGate: 'y5a', noExit: { text: 'Default: Hold. Flag for weekly review.', label: 'UNCATEGORIZED' } },
    { id: 'y5a', question: 'Is it already in the monthly baseline, or does it serve an active revenue/strategic project?', parent: 'y5', yesResult: { text: 'Pay it.' }, noExit: { text: 'Do not add new expenses.' } }
  ],
  'green-yellow': [
    { id: 'gy1', question: 'Is this a credit card payment?', yesGate: 'gy1a' },
    { id: 'gy1a', question: 'Have you been in Yellow or above for 2+ consecutive weeks?', parent: 'gy1', yesResult: { text: 'Pay minimum only.' }, noExit: { text: 'Hold. Not sustained long enough yet.' } },
    { id: 'gy2', question: 'Is this a debt to an individual?', yesGate: 'gy2a' },
    { id: 'gy2a', question: 'Is it the 15th and you have not paid your $50 minimum?', parent: 'gy2', yesResult: { text: 'Pay $50. Or pay more if you want.' }, noExit: { text: 'Optional. Pay more if you want.' } },
    { id: 'gy3', question: 'Is this an outsourcing expense at $25/hr or less, within $1,400/month?', yesResult: { text: 'Spend.' } },
    { id: 'gy4', question: 'Is this discretionary and within your $125/week?', yesResult: { text: 'Spend.' } },
    { id: 'gy5', question: 'Is this a new business expense serving an active project?', yesResult: { text: 'Add it.' }, noExit: { text: 'Default: Hold. Flag for weekly review.', label: 'UNCATEGORIZED' } }
  ],
  green: [
    { id: 'g1', question: 'Is this a credit card payment?', yesGate: 'g1a' },
    { id: 'g1a', question: 'Have you been in sustained Green for 1+ month?', parent: 'g1', yesResult: { text: 'Pay minimum. 2+ months? Start aggressive paydown.' }, noExit: { text: 'Hold. Stay in Green longer first.' } },
    { id: 'g2', question: 'Is this a debt to an individual?', yesResult: { text: 'Pay it. You have the cash. Clear it.' } },
    { id: 'g3', question: 'Is this an outsourcing expense at $25/hr or less, within $2,800/month?', yesResult: { text: 'Spend.' } },
    { id: 'g4', question: 'Is this discretionary and within your $125/week?', yesResult: { text: 'Spend.' } },
    { id: 'g5', question: 'Is this a new expense serving an active or qualifying project?', context: 'Under $50? Spend. Over $50? Flag for review.', yesResult: { text: 'Add it.' }, noExit: { text: 'Under $50: spend. Over $50: hold and flag for review.', label: 'UNCATEGORIZED' } }
  ]
};

const SpendingTree = ({ onClose }) => {
  const [gate1Answer, setGate1Answer] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentPath, setCurrentPath] = useState([]);

  const handleGate1 = (answer) => {
    setGate1Answer(answer);
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    setAnswers({});
    // Initialize with first gate of the zone
    const firstGate = ZONE_GATES[zone].find(g => !g.parent);
    if (firstGate) {
      setCurrentPath([firstGate.id]);
    }
  };

  const handleAnswer = (gateId, answer) => {
    const newAnswers = { ...answers, [gateId]: answer };
    setAnswers(newAnswers);
    
    const gates = ZONE_GATES[selectedZone];
    const gate = gates.find(g => g.id === gateId);
    
    if (answer === 'yes') {
      if (gate.yesResult || gate.yesExit) {
        // Terminal - don't add more to path
      } else if (gate.yesGate) {
        setCurrentPath([...currentPath, gate.yesGate]);
      }
    } else {
      if (gate.noExit) {
        // Terminal
      } else {
        // Find next sequential gate (not a child gate)
        const currentIndex = gates.findIndex(g => g.id === gateId);
        const nextGate = gates.slice(currentIndex + 1).find(g => !g.parent);
        if (nextGate) {
          setCurrentPath([...currentPath, nextGate.id]);
        }
      }
    }
  };

  const resetTree = () => {
    setGate1Answer(null);
    setSelectedZone(null);
    setAnswers({});
    setCurrentPath([]);
  };

  const isGateVisible = (gateId) => {
    return currentPath.includes(gateId);
  };

  const isGateResolved = (gateId) => {
    return answers[gateId] !== undefined;
  };

  const renderGate = (gate) => {
    const isVisible = isGateVisible(gate.id);
    const isResolved = isGateResolved(gate.id);
    const answer = answers[gate.id];
    
    if (!isVisible) return null;

    return (
      <div key={gate.id} className="st-gate-section">
        <div className={`st-gate-card ${isResolved ? 'st-resolved' : ''}`}>
          <div className="st-gate-spine" />
          <div className="st-gate-body">
            <span className="st-gate-label">GATE {gate.id.toUpperCase()} | YES / NO</span>
            <p className="st-gate-question">{gate.question}</p>
            {gate.context && <p className="st-gate-context">{gate.context}</p>}
            <div className="st-gate-actions">
              <button
                className={`st-btn ${isResolved ? (answer === 'yes' ? 'st-btn-chosen' : 'st-btn-unchosen') : 'st-btn-yes'}`}
                onClick={() => !isResolved && handleAnswer(gate.id, 'yes')}
                disabled={isResolved}
              >
                {!isResolved && <Diamond color="#FFFFFF" size="xs" />}
                {gate.yesLabel || 'YES'}
              </button>
              <button
                className={`st-btn ${isResolved ? (answer === 'no' ? 'st-btn-chosen' : 'st-btn-unchosen') : 'st-btn-no'}`}
                onClick={() => !isResolved && handleAnswer(gate.id, 'no')}
                disabled={isResolved}
              >
                {gate.noLabel || 'NO'}
              </button>
            </div>
          </div>
        </div>

        {/* Render YES result/exit */}
        {answer === 'yes' && gate.yesResult && (
          <div className="st-connector-section">
            <div className="st-connector">
              <div className="st-line-down" />
              <span className="st-connector-label st-label-yes">{gate.yesLabel || 'YES'}</span>
              <div className="st-line-down" />
            </div>
            <div className="st-result">
              <Diamond color="#0057FF" size="lg" />
              <p className="st-result-text">{gate.yesResult.text}</p>
            </div>
          </div>
        )}
        {answer === 'yes' && gate.yesExit && (
          <div className="st-connector-section">
            <div className="st-connector">
              <div className="st-line-down" />
              <span className="st-connector-label st-label-yes">{gate.yesLabel || 'YES'}</span>
              <div className="st-line-down" />
            </div>
            <div className="st-exit">
              <p className="st-exit-label">{gate.yesExit.label || 'EXIT'}</p>
              <p className="st-exit-text">{gate.yesExit.text}</p>
              {gate.yesExit.sub && <p className="st-exit-sub">{gate.yesExit.sub}</p>}
            </div>
          </div>
        )}

        {/* Render NO exit */}
        {answer === 'no' && gate.noExit && (
          <div className="st-connector-section">
            <div className="st-connector">
              <div className="st-line-down st-line-neutral" />
              <span className="st-connector-label st-label-no">{gate.noLabel || 'NO'}</span>
              <div className="st-line-down st-line-neutral" />
            </div>
            <div className="st-exit">
              <p className="st-exit-label">{gate.noExit.label || 'EXIT'}</p>
              <p className="st-exit-text">{gate.noExit.text}</p>
              {gate.noExit.sub && <p className="st-exit-sub">{gate.noExit.sub}</p>}
            </div>
          </div>
        )}

        {/* Connector to next gate */}
        {isResolved && !gate.yesResult && !gate.yesExit && !(answer === 'no' && gate.noExit) && (
          <div className="st-connector">
            <div className={`st-line-down ${answer === 'yes' ? '' : 'st-line-neutral'}`} />
            <span className={`st-connector-label ${answer === 'yes' ? 'st-label-yes' : 'st-label-no'}`}>
              {answer === 'yes' ? (gate.yesLabel || 'YES') : (gate.noLabel || 'NO')}
            </span>
            <div className={`st-line-down ${answer === 'yes' ? '' : 'st-line-neutral'}`} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="st-overlay" style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: '#FFFFFF', 
      zIndex: 10000, 
      overflowY: 'auto' 
    }}>
      <div className="st-container" style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '80px 80px 120px',
        position: 'relative'
      }}>
        {/* Close button */}
        <button className="st-close" onClick={onClose} aria-label="Close" style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          width: '48px',
          height: '48px',
          background: '#FFFFFF',
          border: '1px solid #DDDDDD',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#0F0F0F',
          zIndex: 10001
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>

        {/* Header */}
        <div className="st-header">
          <p className="st-brand">
            <Diamond color="#0057FF" size="sm" /> BINARY GAME / RESOURCE RULES SYSTEM
          </p>
          <h1 className="st-title">Should I Spend This Money?</h1>
          <p className="st-subtitle">Select your current cash zone, then walk through the gates. Every purchase that is not groceries, gas, or rent runs through this tree.</p>
        </div>

        <hr className="st-divider" />

        {/* Tree Content */}
        <div className="st-canvas">
          {/* Gate 1: Universal */}
          <p className="st-section-label">GATE 01 / UNIVERSAL</p>
          
          <div className="st-start">
            <Diamond color="#0057FF" size="xs" /> START
          </div>
          
          <div className="st-line-down" />
          
          <div className={`st-gate-card ${gate1Answer ? 'st-resolved' : ''}`}>
            <div className="st-gate-spine" />
            <div className="st-gate-body">
              <span className="st-gate-label">GATE 01 | YES / NO</span>
              <p className="st-gate-question">Is this groceries, gas, or rent?</p>
              <p className="st-gate-context">Not eating out. Not road trips. Groceries for the house, gas for the car, rent for the roof.</p>
              <div className="st-gate-actions">
                <button
                  className={`st-btn ${gate1Answer ? (gate1Answer === 'yes' ? 'st-btn-chosen' : 'st-btn-unchosen') : 'st-btn-yes'}`}
                  onClick={() => !gate1Answer && handleGate1('yes')}
                  disabled={gate1Answer}
                >
                  {!gate1Answer && <Diamond color="#FFFFFF" size="xs" />}
                  YES
                </button>
                <button
                  className={`st-btn ${gate1Answer ? (gate1Answer === 'no' ? 'st-btn-chosen' : 'st-btn-unchosen') : 'st-btn-no'}`}
                  onClick={() => !gate1Answer && handleGate1('no')}
                  disabled={gate1Answer}
                >
                  NO
                </button>
              </div>
            </div>
          </div>

          {/* Gate 1 YES result */}
          {gate1Answer === 'yes' && (
            <div className="st-connector-section">
              <div className="st-connector">
                <div className="st-line-down" />
                <span className="st-connector-label st-label-yes">YES</span>
                <div className="st-line-down" />
              </div>
              <div className="st-result">
                <Diamond color="#0057FF" size="lg" />
                <div>
                  <p className="st-result-text">Spend. No further gates.</p>
                  <p className="st-result-sub">These are locked expenses. They never get cut in any zone.</p>
                </div>
              </div>
            </div>
          )}

          {/* Gate 1 NO - Zone selector */}
          {gate1Answer === 'no' && (
            <>
              <div className="st-connector">
                <div className="st-line-down st-line-neutral" />
                <span className="st-connector-label st-label-no">NO</span>
                <div className="st-line-down st-line-neutral" />
              </div>

              <p className="st-section-label">GATE 02 / ZONE CHECK</p>

              <div className="st-gate-card">
                <div className="st-gate-spine" />
                <div className="st-gate-body">
                  <span className="st-gate-label">GATE 02 | SELECT ZONE</span>
                  <p className="st-gate-question">What zone am I in?</p>
                  <p className="st-gate-context">Check your cash position. Select the zone that matches.</p>
                  <div className="st-zone-buttons">
                    {Object.entries(ZONES).map(([key, zone]) => (
                      <button
                        key={key}
                        className={`st-zone-btn ${selectedZone === key ? 'st-zone-active' : ''}`}
                        onClick={() => handleZoneSelect(key)}
                      >
                        {zone.label}
                        <span className="st-zone-sub">{zone.sublabel}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Zone description */}
              {selectedZone && (
                <div className="st-zone-desc">
                  {ZONES[selectedZone].description}
                </div>
              )}

              {/* Zone path */}
              {selectedZone && (
                <>
                  <div className="st-connector">
                    <div className="st-line-down" />
                    <span className="st-connector-label st-label-yes">{ZONES[selectedZone].label} PATH</span>
                    <div className="st-line-down" />
                  </div>
                  
                  {ZONE_GATES[selectedZone].map(gate => renderGate(gate))}
                </>
              )}
            </>
          )}
        </div>

        {/* Reset bar */}
        {(gate1Answer || selectedZone) && (
          <div className="st-reset-bar">
            <button className="st-reset-btn" onClick={resetTree}>RESET TREE</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendingTree;
